/**
 * Scroll speed: in-scene vs base multiplier.
 * Chapter-title overlay is handled separately by setupChapterTitleOverlay.
 */

import type Lenis from 'lenis'
import { areAnimationsEnabled } from '../../utils/animation-settings'
import type { ScrollSpeedConfig, ScrollSpeedInstance } from './types'

const SCENE_SELECTOR = '.scene, .chapter-title-scene, .minigame-scene'
const CHAPTER_TITLE_MARGIN_VH = 0.5

function findSceneContainingViewportCenter(
  viewportCenterY: number,
  fromTop: number,
  fromBottom: number,
  lastScene: HTMLElement | null
): { scene: HTMLElement | null; isInZone: boolean } {
  if (lastScene) {
    const rect = lastScene.getBoundingClientRect()
    if (rect.top <= viewportCenterY && rect.bottom >= viewportCenterY) {
      const zoneTop = rect.top + fromTop
      const zoneBottom = rect.bottom - fromBottom
      const isInZone = viewportCenterY >= zoneTop && viewportCenterY <= zoneBottom
      return { scene: lastScene, isInZone }
    }
  }
  const scenes = Array.from(document.querySelectorAll(SCENE_SELECTOR)) as HTMLElement[]
  for (const scene of scenes) {
    const rect = scene.getBoundingClientRect()
    if (rect.top <= viewportCenterY && rect.bottom >= viewportCenterY) {
      const zoneTop = rect.top + fromTop
      const zoneBottom = rect.bottom - fromBottom
      const isInZone = viewportCenterY >= zoneTop && viewportCenterY <= zoneBottom
      return { scene, isInZone }
    }
  }
  return { scene: null, isInZone: false }
}

function computeChapterTitleProgress(viewportCenterY: number, marginPx: number, sectionTop: number, sectionBottom: number): number {
  if (viewportCenterY < sectionTop - marginPx) return 0
  if (viewportCenterY < sectionTop) return (viewportCenterY - (sectionTop - marginPx)) / marginPx
  if (viewportCenterY <= sectionBottom) return 1
  if (viewportCenterY < sectionBottom + marginPx) return 1 - (viewportCenterY - sectionBottom) / marginPx
  return 0
}

export function setupScrollSpeedControl(
  lenisInstance: Lenis | null,
  getScrollSpeedConfig: () => ScrollSpeedConfig | null,
  frameCallbacks: (() => void)[],
  appElement: HTMLElement
): ScrollSpeedInstance {
  if (!lenisInstance) return { destroy: () => {}, update: () => {} }

  let lastSceneElement: HTMLElement | null = null
  const originalScrollTo = lenisInstance.scrollTo.bind(lenisInstance)

  lenisInstance.scrollTo = (target: number | string, options?: unknown) => {
    if (typeof target === 'number' && lenisInstance) {
      const currentScroll = lenisInstance.scroll
      const delta = target - currentScroll
      return originalScrollTo(currentScroll + delta, options)
    }
    return originalScrollTo(target, options)
  }

  const updateScrollSpeed = () => {
    if (!lenisInstance) return
    const config = getScrollSpeedConfig()
    if (!config) return
    const viewportCenterY = window.innerHeight / 2
    const fromTop = config.fromTop ?? 0
    const fromBottom = config.fromBottom ?? 0
    const { scene, isInZone } = findSceneContainingViewportCenter(viewportCenterY, fromTop, fromBottom, lastSceneElement)
    lastSceneElement = scene
    const wheelMultiplier = isInZone ? config.inScene : config.base
    const touchMultiplier = isInZone ? config.inSceneMobile : config.baseMobile
    if (Math.abs(lenisInstance.options.wheelMultiplier - wheelMultiplier) > 0.001 ||
        Math.abs(lenisInstance.options.touchMultiplier - touchMultiplier) > 0.001) {
      lenisInstance.options.wheelMultiplier = wheelMultiplier
      lenisInstance.options.touchMultiplier = touchMultiplier
    }
  }

  updateScrollSpeed()
  frameCallbacks.push(updateScrollSpeed)
  const resizeHandler = () => updateScrollSpeed()
  window.addEventListener('resize', resizeHandler, { passive: true })

  return {
    destroy: () => {
      if (lenisInstance) lenisInstance.scrollTo = originalScrollTo
      const i = frameCallbacks.indexOf(updateScrollSpeed)
      if (i > -1) frameCallbacks.splice(i, 1)
      window.removeEventListener('resize', resizeHandler)
    },
    update: updateScrollSpeed,
  }
}

// ---------------------------------------------------------------------------
// Chapter-title yellow overlay — works with or without Lenis / animations
// ---------------------------------------------------------------------------

export interface ChapterTitleOverlayInstance {
  destroy: () => void
}

export function setupChapterTitleOverlay(
  appElement: HTMLElement,
  frameCallbacks: (() => void)[],
): ChapterTitleOverlayInstance {
  let overlay = appElement.querySelector('.chapter-title-overlay') as HTMLElement | null
  if (!overlay) {
    overlay = document.createElement('div')
    overlay.className = 'chapter-title-overlay'
    overlay.setAttribute('aria-hidden', 'true')
    appElement.insertBefore(overlay, appElement.firstChild)
  }

  const animated = areAnimationsEnabled()

  const update = () => {
    if (!overlay) return
    const vh = window.innerHeight
    const viewportCenterY = vh / 2
    const marginPx = CHAPTER_TITLE_MARGIN_VH * vh
    const scenes = Array.from(document.querySelectorAll('.chapter-title-scene, .epilogue-scene-wrapper'))
    let maxProgress = 0
    for (const scene of scenes) {
      const rect = scene.getBoundingClientRect()
      maxProgress = Math.max(maxProgress, computeChapterTitleProgress(viewportCenterY, marginPx, rect.top, rect.bottom))
    }
    overlay.style.opacity = String(animated ? maxProgress : (maxProgress > 0 ? 1 : 0))
  }

  frameCallbacks.push(update)

  let rafId: number | null = null
  const scrollFallback = () => {
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        update()
        rafId = null
      })
    }
  }
  window.addEventListener('scroll', scrollFallback, { passive: true })

  update()

  return {
    destroy: () => {
      const i = frameCallbacks.indexOf(update)
      if (i > -1) frameCallbacks.splice(i, 1)
      window.removeEventListener('scroll', scrollFallback)
      if (rafId !== null) cancelAnimationFrame(rafId)
    },
  }
}
